import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Form, Select, Button, message, Table, Col, Row, Spin, Modal, Input, DatePicker, Tooltip } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';

interface VivaEngageReport {
    nomination_id: string;
    feed_type: string;
    reward_name: string;
    nominator_name: string;
    nominee_name: string;
    citation: string;
    header_text: string;
    viva_engage_status: string;
}


const VivaEngage: React.FC = () => {
    const { instance, accounts } = useMsal();
    const [rewardTypeId, setRewardTypeId] = useState<string>('');
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [vivaEngageReportData, setVivaEngageReportData] = useState<VivaEngageReport[]>([]);
    const [items, setRewardTypesData] = useState<[]>([]);
    const [loading, setLoading] = useState<boolean>(false); // Loading state
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState<{ header_text: string; citation: string } | null>(null);
    // Add a state to track disabled rows
    const [disabledRows, setDisabledRows] = useState<Set<string>>(new Set());
    const [searchText, setSearchText] = useState<string>('');
    const [filteredData, setFilteredData] = useState<VivaEngageReport[]>([]);
    const [pageSize, setPageSize] = useState<number>(50);
  
    const API_URL = process.env.REACT_APP_API_URL;
    const userId = localStorage.getItem("UserId");

    const sentMessageToYammer = async (postmessage: string) => {
        try{
            const token = await fetchToken();
            const response = await axios.post(`${API_URL}/api/VivaEngage/PostMessageToYammer`,
                null,
                {
                    params: {
                        post_message: `${postmessage + "\n👏👏"} `,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

            if (response.status === 200) {
                message.success('Data pushed to Viva Engage successfully');
            } else {
                message.error('Failed to push data to Yammer');
            }
        }
        catch (error) {
            console.error('Failed to send message to Yammer:', error);
        }
    };

    useEffect(() => {
          fetchToken();
          getRewardTypeData();
    }, []);

    const handlePreview = (record: VivaEngageReport) => {
        setModalContent({ header_text: record.header_text, citation: record.citation });
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setModalContent(null);
    };
  
    const fetchToken = async () => {
            const res = await instance.acquireTokenSilent({
              ...loginRequest,
              account: accounts[0]
            });
          return res.idToken;
      };

      const getRewardTypeData = async () => {
        try {
          const token = await fetchToken();
          const response = await axios.get(`${API_URL}/api/VivaEngage/GetRewardTypes`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          
          // Sort reward types by text (reward name) in ascending order
          const sortedData = response.data.sort((a: any, b: any) => 
            a.text.localeCompare(b.text)
          );
          
          setRewardTypesData(sortedData);
          console.log('Reward types data:', sortedData); // Log the sorted reward types data
        } catch (error) {
          message.error('Failed to load reward type data');
        }
      }

      const handleRewardTypeChange = (value: string) => {
        setRewardTypeId(value);
      }

      const handleYearChange = (date: Dayjs | null) => {
        if (date) {
          setSelectedYear(date.year());
        } else {
          setSelectedYear(null);
        }
      }

 
      const handleSubmit = async () => {
          if (rewardTypeId === 'Select' || rewardTypeId === '') {
                message.error('Please select a reward type');
                return;
            }
          if (!selectedYear) {
                message.error('Please select a year');
                return;
            }
          setLoading(true); // Start loading
          LoadFeedData();
      };

      const LoadFeedData = async () => {
        try 
          {
              const token = await fetchToken();
              const response = await axios.get(`${API_URL}/api/VivaEngage/GetVivaEngageData/${rewardTypeId}/${selectedYear}`, {
                  headers: {
                      Authorization: `Bearer ${token}`,
                      'Content-Type': 'application/json',
                  },
              });

              const data = response.data;

              // Update the table data
              setVivaEngageReportData(response.data);
              setFilteredData(response.data);

              // Update the disabled rows based on the status
                const disabledSet = new Set<string>();
                data.forEach((record: VivaEngageReport) => {
                    if (record.viva_engage_status?.toLowerCase() === 'approved' || record.viva_engage_status?.toLowerCase() === 'rejected') {
                        disabledSet.add(record.nomination_id);
                    }
        });
        setDisabledRows(disabledSet);
          } catch (error) 
          {
              message.error(`Failed to get report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
          finally {
            setLoading(false); // Stop loading
        }
      }

      const handleApprove = async (record: VivaEngageReport) => {
        Modal.confirm({
            title: 'Are you sure you want to push to viva engage?',
            onOk: async () => {
                setLoading(true); // Start loading
                try {
                    const token = await fetchToken();
                    const response = await axios.put(`${API_URL}/api/VivaEngage/UpdateStatusData`,
                        null,
                        {
                            params: {
                                nomination_id: record.nomination_id,
                                status: 'Approved',
                                user_id: userId,
                            },
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        });
                    if (response.status === 200) {
                        LoadFeedData(); // Refresh the data after updating status
                        await sentMessageToYammer(record.header_text + "\n\n"+ record.citation); 
                        
                         // Add the row to the disabled set
                         setDisabledRows((prev) => new Set(prev).add(record.nomination_id));
                    }
                } catch (error) {
                    message.error(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                    setLoading(false); // Stop loading
                }
            },
        });
    };

    const handleReject = async (record: VivaEngageReport) => {
        Modal.confirm({
            title: 'Are you sure you want to reject this feed?',
            onOk: async () => {
                setLoading(true); // Start loading
                try {
                    const token = await fetchToken();
                    const response = await axios.put(`${API_URL}/api/VivaEngage/UpdateStatusData`,
                        null,
                        {
                            params: {
                                nomination_id: record.nomination_id,
                                status: 'Rejected',
                                user_id: userId,
                            },
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        });
                    if (response.status === 200) {
                        message.success('Data rejected successfully');
                        LoadFeedData(); // Refresh the data after updating status

                         // Add the row to the disabled set
                         setDisabledRows((prev) => new Set(prev).add(record.nomination_id));
                    }
                } catch (error) {
                    message.error(`Failed to update status: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                    setLoading(false); // Stop loading
                }
            },
        });
    };

      // Search functionality
      useEffect(() => {
          if (searchText === '') {
              setFilteredData(vivaEngageReportData);
          } else {
              const filtered = vivaEngageReportData.filter(record => {
                  // Get the display text for status
                  const getStatusDisplayText = (status: string | null) => {
                      if (status === null) return 'Not Sent';
                      if (status.toLowerCase() === 'approved') return 'Approved';
                      if (status.toLowerCase() === 'rejected') return 'Rejected';
                      return status;
                  };

                  const statusDisplayText = getStatusDisplayText(record.viva_engage_status);

                  return (
                      record.citation.toLowerCase().includes(searchText.toLowerCase()) ||
                      record.header_text.toLowerCase().includes(searchText.toLowerCase()) ||
                      record.nominator_name.toLowerCase().includes(searchText.toLowerCase()) ||
                      record.nominee_name.toLowerCase().includes(searchText.toLowerCase()) ||
                      record.reward_name.toLowerCase().includes(searchText.toLowerCase()) ||
                      statusDisplayText.toLowerCase().includes(searchText.toLowerCase())
                  );
              });
              setFilteredData(filtered);
          }
      }, [searchText, vivaEngageReportData]);
  
      const columns = [
        {
            title: 'Citation',
            dataIndex: 'citation',
            key: 'citation',
            sorter: (a: VivaEngageReport, b: VivaEngageReport) => a.citation.localeCompare(b.citation),
            render: (text: string) => (
                <Tooltip title={text} overlayStyle={{ maxWidth: '1000px' }}>
                    <span>{text.length > 50 ? `${text.substring(0, 50)}...` : text}</span>
                </Tooltip>
            ),
        },
        {
            title: 'Header Text',
            dataIndex: 'header_text',
            key: 'header_text',
            sorter: (a: VivaEngageReport, b: VivaEngageReport) => a.header_text.localeCompare(b.header_text),
        },
        {
            title: 'Status',
            dataIndex: 'viva_engage_status',
            key: 'viva_engage_status',
            sorter: (a: VivaEngageReport, b: VivaEngageReport) => {
                const getStatusOrder = (status: string | null) => {
                    if (status === null) return 0;
                    if (status.toLowerCase() === 'approved') return 1;
                    if (status.toLowerCase() === 'rejected') return 2;
                    return 3;
                };
                return getStatusOrder(a.viva_engage_status) - getStatusOrder(b.viva_engage_status);
            },
            render: (status: string | null) => {
                let color = '';
                let displayText = '';
        
                if (status === null) {
                    color = 'gray';
                    displayText = 'Not Sent';
                } else if (status.toLowerCase() === 'approved') {
                    color = 'green';
                    displayText = 'Approved';
                } else if (status.toLowerCase() === 'rejected') {
                    color = 'red';
                    displayText = 'Rejected';
                }
        
                return (
                    <span style={{ color: color, fontWeight: 'bold' }}>
                        {displayText}
                    </span>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: VivaEngageReport) => (
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button 
                        type="primary" 
                        onClick={() => handleApprove(record)}
                        disabled={disabledRows.has(record.nomination_id)} // Disable if the row is in the disabled set
                    >
                        Approve
                    </Button>
                    <Button 
                        type="primary" 
                        danger 
                        onClick={() => handleReject(record)}
                        disabled={disabledRows.has(record.nomination_id)} // Disable if the row is in the disabled set
                    >
                        Reject
                    </Button>
                    <Button
                        type="default"
                        onClick={() => handlePreview(record)}
                    >
                        Preview
                    </Button>
                </div>
            ),
        }
    ];
  
     return (
      <div>
          <div>
              <Form onFinish={handleSubmit}>
                  <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-end', gap: 16 }}>
                      <div style={{ flex: '1 1 auto', minWidth: 300 }}>
                          <Form.Item label="Reward Type" required style={{ marginBottom: 0 }}>
                            <Select
                                value={rewardTypeId || undefined}
                                onChange={handleRewardTypeChange}
                                placeholder="Select"
                                size="large"
                                style={{ width: '100%' }}
                                popupMatchSelectWidth={false}
                            >
                                {items.map((type: any) => (
                                    <Select.Option key={type.value} value={type.value}>
                                        {type.text}
                                    </Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                      </div>
                      <div style={{ flex: '0 0 160px' }}>
                          <Form.Item label="Year" required style={{ marginBottom: 0 }}>
                            <DatePicker
                                value={selectedYear ? dayjs().year(selectedYear) : null}
                                onChange={handleYearChange}
                                picker="year"
                                size="large"
                                placeholder="Select Year"
                                style={{ width: '100%' }}
                                disabledDate={(current) => current && current.year() < 2025}
                            />
                          </Form.Item>
                      </div>
                      <div style={{ flex: '0 0 auto' }}>
                          <Form.Item style={{ marginBottom: 0 }}>
                              <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                disabled={!rewardTypeId || !selectedYear}
                              >
                                  Get Data
                              </Button>
                          </Form.Item>
                      </div>
                  </div>
              </Form>
          </div>
          <div>
            {loading ? (
                    <Spin style={{ display: 'block', margin: '20px auto' }} /> // Show spinner while loading
                ) : (
                    <>
                        <Input.Search
                            placeholder="Search by citation, header text, or status"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={(value) => setSearchText(value)}
                            style={{ marginTop: '10px', marginBottom: '10px' }}
                            allowClear
                        />
                        <Table
                            dataSource={filteredData}
                            columns={columns}
                            rowKey={(record) => record.nomination_id} // Ensure unique key
                            pagination={{ 
                                pageSize: pageSize,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '25', '50', '100'],
                                onShowSizeChange: (current, size) => setPageSize(size)
                            }}
                            scroll={{ x: 'max-content' }}
                            sticky
                            style={{ border: '1px solid #ddd' }} // Example inline styles
                        />
                    </>
            )}
          </div>
          <div>
            {/* ...existing JSX... */}
            <Modal
                title={null}
                visible={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800} // Set the width of the modal (adjust as needed)
            >
                {modalContent && (
                    <div>
                        {modalContent.header_text} <br /><br />
                        {modalContent.citation} <br />👏👏
                    </div>
                )}
            </Modal>
        </div>
      </div>
    );
  };
  
  export default VivaEngage;